import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChatCircleDots,
  Flag,
  Handshake,
  TrendUp,
  Users,
} from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import SeoContextFooter from "@/components/SeoContextFooter";
import { DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { scoreToRating } from "@/lib/scoreGrade";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import relationshipMemoryEditorialDesktop from "@/assets/relationship-memory-editorial-1600.webp";
import relationshipMemoryEditorialMobile from "@/assets/relationship-memory-editorial-1000.webp";

const source = "product_relationship_memory";
const memoryLayers = [
  {
    icon: Users,
    label: "Stakeholders",
    title: "Remember who matters and how they entered the story.",
    copy: "Keep the champion, approver, blocker, handoff, and ownership changes attached to the relationship instead of scattered across notes.",
  },
  {
    icon: Handshake,
    label: "Commitments",
    title: "Carry promises and open loops forward.",
    copy: "Know what you promised, what the customer promised, what is still unresolved, and which follow-up should shape the next conversation.",
  },
  {
    icon: Flag,
    label: "Risks and objections",
    title: "See what is still alive instead of rereading everything.",
    copy: "A topic being mentioned once is not the same as an objection being resolved. Callsect keeps the active relationship state visible.",
  },
  {
    icon: TrendUp,
    label: "What changed",
    title: "Understand the movement across calls.",
    copy: "Track when urgency rises, confidence weakens, ownership changes, or the relationship moves toward expansion, renewal, or risk.",
  },
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

function RelationshipStateProof() {
  const state = DEMO_RELATIONSHIP_DATA.relationship_memory?.current_state || {};
  const facts = state.facts || {};
  const inferences = state.inferences || {};
  const latestChangeSummary =
    DEMO_RELATIONSHIP_DATA.milestones?.find((milestone) => milestone.call_id === state.as_of_call_id)
      ?.relationship_update?.changes?.summary || [];
  const whatChanged = latestChangeSummary.slice(0, 2).join(". ");

  return (
    <article
      className="product-relationship-proof"
      aria-label="Relationship state preview from the AM / CSM interactive demo"
    >
      <div className="product-relationship-proof__head">
        <span><Handshake size={18} weight="duotone" aria-hidden="true" /> Relationship state</span>
        <strong>Know where the relationship stands now.</strong>
      </div>
      <div className="product-relationship-proof__status">
        <span>Status quo</span>
        <p>{facts.customer_position}</p>
      </div>
      <div className="product-relationship-proof__change">
        <span>What changed</span>
        <p>{whatChanged}{whatChanged ? "." : ""}</p>
      </div>
      <div className="product-relationship-proof__signals">
        <span>Current signals</span>
        <div className="product-relationship-proof__signals-row" aria-label="Current commercial signals">
          <span>Buying intent <strong>{scoreToRating(inferences.buying_intent?.score)}</strong></span>
          <span>Churn risk <strong>{scoreToRating(inferences.churn_risk?.score)}</strong></span>
          <span>Trust <strong>{scoreToRating(inferences.trust?.score)}</strong></span>
        </div>
      </div>
    </article>
  );
}

export default function ProductRelationshipMemoryPage() {
  return (
    <ProductPageShell source={source}>
      <section className="product-feature-hero product-feature-hero--relationship-memory px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-relationship-memory-art pointer-events-none absolute inset-0" aria-hidden="true">
          <picture className="product-relationship-memory-art__picture">
            <source media="(max-width: 600px)" srcSet={relationshipMemoryEditorialMobile} />
            <img
              className="product-relationship-memory-art__image"
              src={relationshipMemoryEditorialDesktop}
              alt=""
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </div>

        <div className="product-feature-hero__layout relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-start lg:gap-16">
          <div className="product-feature-hero__copy">
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">Keep every customer</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">relationship moving.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy mt-7 max-w-2xl">
              Callsect carries forward the people, promises, risks, objections, and changes that should still matter when the next conversation starts.
            </p>
          </div>

          <RelationshipStateProof />
        </div>

        <div className="product-feature-hero__actions mx-auto flex flex-col gap-3 sm:flex-row">
          <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
            Sign up free
          </Link>
          <Link to="/demo" onClick={() => track("hero", "relationship_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
            See Demo
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="product-feature-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="product-feature-section__intro">
            <span>What stays with the customer</span>
            <h2>Not a pile of old notes. A usable relationship state.</h2>
          </div>
          <div className="product-feature-grid product-feature-grid--four mt-12">
            {memoryLayers.map(({ icon: Icon, label, title, copy }) => (
              <article className="product-feature-card" key={label}>
                <Icon size={24} weight="duotone" aria-hidden="true" />
                <span>{label}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band marketing-highlight-band--with-context mx-auto flex max-w-6xl flex-col gap-6 rounded-2xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <ChatCircleDots size={25} weight="duotone" aria-hidden="true" />
            <h2>Walk into the next call knowing where you left off.</h2>
          </div>
          <Link to="/product/prepare-next-call" className="product-feature-inline-link">
            See next-call preparation
            <ArrowRight size={17} weight="bold" />
          </Link>
          <SeoContextFooter
            label="Related guide"
            to="/conversation-intelligence-vs-call-summarization"
            onClick={() => track("internal_link", "conversation_intelligence_vs_call_summarization")}
          >
            Conversation intelligence vs call summarization
          </SeoContextFooter>
        </div>
      </section>
    </ProductPageShell>
  );
}
